import React, { useState, useEffect } from 'react';
import {
  Box,
  Input,
  Table,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Card,
  Alert
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import DisposablePopup from '../components/DisposablePopup';

const Disposables = () => {
  const [disposables, setDisposables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDisposable, setSelectedDisposable] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchDisposables();
  }, [searchTerm]);

  const fetchDisposables = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;

      const response = await axiosInstance.get('/disposables', { params });
      setDisposables(response.data.disposables || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch disposables');
    } finally {
      setLoading(false);
    }
  };

  const handleDisposableClick = (disposableId) => {
    setSelectedDisposable(disposableId);
  };

  const handleClosePopup = () => {
    setSelectedDisposable(null);
    fetchDisposables();
  };

  const totalPages = Math.ceil(disposables.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDisposables = disposables.slice(startIndex, startIndex + itemsPerPage);

  return (
    <Box sx={{ padding: 3 }}>
      <Typography level="h1" sx={{ mb: 3, color: '#eb7a24' }}>
        Disposable Materials
      </Typography>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, p: 2 }}>
        <FormControl sx={{ maxWidth: 400 }}>
          <FormLabel>Search by Disposable Name</FormLabel>
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search disposables..."
            sx={{
              '&:hover': { borderColor: '#ea7a23' },
              '&:focus-within': { borderColor: '#ea7a23' }
            }}
          />
        </FormControl>
      </Card>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography level="body-md">
              Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, disposables.length)} of {disposables.length} disposables
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                sx={{
                  borderColor: '#eb7a24',
                  color: '#eb7a24',
                  '&:hover': { borderColor: '#cc6a1f', backgroundColor: 'rgba(235, 122, 36, 0.1)' }
                }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(currentPage + 1)}
                sx={{
                  borderColor: '#eb7a24',
                  color: '#eb7a24',
                  '&:hover': { borderColor: '#cc6a1f', backgroundColor: 'rgba(235, 122, 36, 0.1)' }
                }}
              >
                Next
              </Button>
            </Box>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Table
              sx={{
                '& thead th': {
                  backgroundColor: '#eb7a24',
                  color: 'white',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                },
                '& tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9'
                },
                '& tbody tr:hover': {
                  backgroundColor: '#fff4e6',
                  cursor: 'pointer'
                },
                '& td': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }
              }}
            >
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>Disposable Name</th>
                  <th style={{ width: '20%' }}>Vendor</th>
                  <th style={{ width: '15%' }}>Rate (per item)</th>
                  <th style={{ width: '15%' }}>Current Quantity</th>
                  <th style={{ width: '25%' }}>Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDisposables.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                      No disposables found
                    </td>
                  </tr>
                ) : (
                  paginatedDisposables.map((disposable) => (
                    <tr
                      key={disposable.id}
                      onClick={() => handleDisposableClick(disposable.id)}
                    >
                      <td style={{ color: '#eb7a24', fontWeight: 'bold' }}>
                        {disposable.disposable_name}
                      </td>
                      <td>{disposable.vendor_name || 'N/A'}</td>
                      <td>{disposable.rate != null ? Number(disposable.rate).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}</td>
                      <td>{disposable.quantity}</td>
                      <td>
                        {disposable.last_updated
                          ? new Date(disposable.last_updated).toLocaleDateString()
                          : 'N/A'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Box>
        </>
      )}

      {selectedDisposable && (
        <DisposablePopup
          disposableId={selectedDisposable}
          onClose={handleClosePopup}
        />
      )}
    </Box>
  );
};

export default Disposables;

